import { Link } from "react-router-dom";
import WaveDivider from "../../home/components/WaveDivider";
import type { ServicePageData } from "../../../mocks/servicePages";

const badgeBg: Record<string, string> = {
  sameday: "bg-cl-green text-white border-[#4a8c0a]",
  nextday: "bg-cl-lime text-cl-dark border-cl-green",
  new: "bg-cl-dark text-white border-[#09245b]",
};

interface ServiceHeroProps {
  service: ServicePageData;
  onBookNow: () => void;
}

export default function ServiceHero({ service, onBookNow }: ServiceHeroProps) {
  return (
    <div className="relative">
      <section
        className="relative w-full min-h-[75vh] overflow-hidden flex flex-col"
        style={{ background: "#09245b" }}
      >
        <img
          src={service.heroImage}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(9,36,91,0.92) 0%, rgba(9,36,91,0.80) 50%, rgba(45,80,14,0.75) 100%)" }}
        />

        <div className="relative flex-1 flex flex-col max-w-screen-xl mx-auto px-6 lg:px-10 w-full pt-32 pb-16">
          <div className="flex items-center gap-2 mb-8">
            <Link to="/" className="text-white/50 text-sm font-semibold hover:text-white transition-colors cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-white/40 text-sm"></i>
            <span className="text-white/80 text-sm font-semibold">{service.title}</span>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              <div className={`inline-flex items-center gap-2 text-xs font-black px-4 py-1.5 rounded-full border-2 mb-6 cartoon-shadow-sm ${badgeBg[service.badgeType]}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse inline-block"></span>
                {service.badge}
              </div>

              <h1 className="text-5xl md:text-6xl font-black leading-tight mb-4">
                <span className="block text-white">{service.title}</span>
                <span className="block text-cl-lime">
                  {service.headline}
                </span>
              </h1>

              <p className="text-white/60 text-lg font-semibold mb-3">{service.subheadline}</p>
              <p className="text-white/70 text-base leading-relaxed mb-8 max-w-lg">{service.description}</p>

              <div className="inline-flex items-center gap-3 bg-white/10 border-2 border-white/20 rounded-2xl px-5 py-3 mb-8">
                <i className="ri-price-tag-3-line text-cl-lime text-xl"></i>
                <div>
                  <p className="text-white/60 text-xs">{service.title} collection</p>
                  <p className="text-white font-black text-xl leading-none">{service.priceFrom}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={onBookNow}
                  className="bg-cl-green text-white font-black px-8 py-4 rounded-xl text-sm cartoon-btn-green whitespace-nowrap">
                  Book Now — Get a Fixed Price
                </button>
                <a
                  href="https://wa.me/443335775445?text=Hi%20Cleario%21%20I%27d%20like%20an%20instant%20quote.%20Here%20are%20my%20items%3A"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-black px-7 py-4 rounded-xl text-sm cartoon-btn whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  Get an Instant Quote via WhatsApp
                </a>
                <a href="tel:03335775445"
                  className="flex items-center justify-center gap-2 bg-white/10 border-2 border-white/25 text-white font-black px-7 py-4 rounded-xl text-sm cartoon-btn whitespace-nowrap">
                  <i className="ri-phone-fill text-cl-lime"></i>
                  03335 775445
                </a>
              </div>
            </div>

            <div className="hidden lg:block w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl p-6 cartoon-card">
                <img src={service.productImage} alt={service.title} className="w-full h-56 object-contain" />
              </div>
              <div className="flex gap-3 mt-4">
                <div className="flex-1 bg-cl-lime rounded-xl p-3 thick-border cartoon-shadow-sm text-center">
                  <i className="ri-star-fill text-cl-dark text-lg block mb-0.5"></i>
                  <p className="text-cl-dark font-black text-sm leading-none">4.8/5</p>
                  <p className="text-cl-dark/60 text-xs mt-0.5">2,000+ reviews</p>
                </div>
                <div className="flex-1 bg-cl-lime rounded-xl p-3 thick-border cartoon-shadow-sm text-center">
                  <i className="ri-recycle-line text-cl-dark text-lg block mb-0.5"></i>
                  <p className="text-cl-dark font-black text-sm leading-none">&gt;95%</p>
                  <p className="text-cl-dark/60 text-xs mt-0.5">Recycled</p>
                </div>
                <div className="flex-1 bg-cl-lime rounded-xl p-3 thick-border cartoon-shadow-sm text-center">
                  <i className="ri-shield-check-line text-cl-dark text-lg block mb-0.5"></i>
                  <p className="text-cl-dark font-black text-sm leading-none">Licensed</p>
                  <p className="text-cl-dark/60 text-xs mt-0.5">CBDU61392</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaveDivider fromColor="#09245b" toColor="#6CC412" />
    </div>
  );
}
